/**
 * Whether an ISO 8601 date-time string names the instant it means — i.e. whether
 * it carries a zone designator (`Z`/`z`) or a numeric offset (`±HH:mm`,
 * `±HHmm`, or `±HH`).
 *
 * This exists because `Date.parse` is not a validator. Per ECMA-262, a date-time
 * form with no offset is interpreted as **local time**, and a date-only form as
 * UTC — so `Date.parse('2026-09-10T15:00')` succeeds on any machine and yields a
 * different instant on each one. A string that survives `Date.parse` has
 * therefore proven nothing about *which* 3pm it means.
 *
 * A close instant is a `timestamptz`. Writing a zone-less wall-clock string into
 * one lets Postgres pick the zone, which is how a Central auction's "3:00 PM"
 * landed at 15:00 UTC — five hours early (BUG-1461).
 *
 * **Every real offset form counts, not just ISO 8601 *extended* (BUG-1487).**
 * The question this answers is "does the string name its zone", and
 * `2026-08-11 01:55:00+00` — Postgres' own `timestamptz` text output, and what
 * the timed-controls API returns — names it as unambiguously as `+00:00` does.
 * Matching extended form alone rejected it, which would have re-broken BUG-1194
 * the moment this predicate reached `ChangeCloseTimeSchema` (sign-off: Michael
 * Lawrie, 2026-09-06).
 *
 * The time is matched as part of the pattern, immediately before the designator,
 * and that adjacency is load-bearing: a bare `±HH` offset is only an OFFSET when
 * it follows a time. Match the designator alone and a date-only `2026-09-10`
 * reads its own `-10` as a zone — the exact false accept this predicate exists
 * to prevent. Testing for a time *anywhere* in the string is not enough either:
 * in `2030-06-01+05:00` the offset's OWN colon satisfies that, so the date-only
 * string passes as zoned (caught in review on BUG-1487).
 *
 * Shape only. It does not judge whether the instant is real — parseability is
 * `Date.parse`'s job in the caller, and keeping the two separate is what lets a
 * caller report them as different issues.
 *
 * **That separation is also what makes the non-extended forms safe to accept.**
 * `±HHmm` and bare `±HH` are outside ECMA-262's Date Time String Format, so
 * `Date.parse` handling them is implementation-defined (V8 does; the spec does
 * not require it). This predicate never has to care, because every caller runs
 * the parse check FIRST and only reaches the zone check on a string that already
 * parsed — so on a runtime that rejects `-0500`, the string is refused as
 * unparseable rather than false-accepted as zoned. Accepting the shape can widen
 * what a caller *asks about*; it cannot widen what a caller *accepts*. Keep that
 * ordering when adding a caller.
 *
 * @param value - The raw string to inspect. Not parsed; only its shape is read.
 * @returns `true` when the string ends in a time immediately followed by `Z`,
 *   `z`, or a `±HH:mm` / `±HHmm` / `±HH` offset.
 * @sideEffect none — pure.
 */
export function carriesZoneOffset(value: string): boolean {
  return /\d{1,2}:\d{2}(:\d{2})?(\.\d+)?([Zz]|[+-]\d{2}(:?\d{2})?)$/.test(value.trim());
}
